/**
 * CurriculumHub.tsx
 * Standalone Curriculum Hub page — teachers manage their saved curricula.
 *
 * Scope tabs: My Curriculum | Team/PLC | School | District
 * Each card shows title, subject, grade, publisher, source type.
 * Actions: Use in Lesson, Delete.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Library, Upload, FileText, BookMarked, Trash2, ArrowRight,
  RefreshCw, AlertCircle, Plus, Filter, Pencil, Check, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  listCurricula,
  deleteCurriculum,
  renameCurriculum,
  type CurriculumSummary,
} from './curriculum-api';

// ── Types ─────────────────────────────────────────────────────────────────────

type Scope = 'my' | 'team' | 'school' | 'district';

const SCOPE_TABS: { id: Scope; label: string }[] = [
  { id: 'my',       label: 'My Curriculum' },
  { id: 'team',     label: 'Team / PLC' },
  { id: 'school',   label: 'School' },
  { id: 'district', label: 'District' },
];

const SOURCE_LABELS: Record<string, string> = {
  upload:          'File Upload',
  google_docs:     'Google Docs',
  google_slides:   'Google Slides',
  url:             'URL',
};

const SOURCE_ICONS: Record<string, string> = {
  upload:        '📄',
  google_docs:   '📝',
  google_slides: '📊',
  url:           '🔗',
};

// ── Props ─────────────────────────────────────────────────────────────────────

export interface CurriculumHubProps {
  userId: string;
  onUseInLesson: (id: number) => void;
  onUploadNew: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CurriculumHub({ userId, onUseInLesson, onUploadNew }: CurriculumHubProps) {
  const [scope, setScope]           = useState<Scope>('my');
  const [items, setItems]           = useState<CurriculumSummary[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting]     = useState(false);
  const [deleteError, setDeleteError]  = useState<string | null>(null);
  const [renameId, setRenameId]        = useState<number | null>(null);
  const [renameVal, setRenameVal]      = useState('');
  const renameRef                      = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listCurricula(userId, scope);
      setItems(data);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to load curricula.');
    } finally {
      setLoading(false);
    }
  }, [userId, scope]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(item => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      item.title?.toLowerCase().includes(q) ||
      item.subject?.toLowerCase().includes(q) ||
      item.gradeLevel?.toLowerCase().includes(q) ||
      item.publisher?.toLowerCase().includes(q) ||
      item.fileName?.toLowerCase().includes(q)
    );
  });

  async function handleDelete(id: number) {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteCurriculum(id, userId);
      setItems(prev => prev.filter(i => i.id !== id));
      setDeleteConfirmId(null);
    } catch (err) {
      setDeleteError((err as Error).message ?? 'Delete failed. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  function startRename(id: number, currentTitle: string) {
    setRenameId(id);
    setRenameVal(currentTitle || '');
    setTimeout(() => { renameRef.current?.focus(); renameRef.current?.select(); }, 40);
  }

  async function commitRename(id: number) {
    const next = renameVal.trim();
    setRenameId(null);
    if (!next) return;
    try {
      await renameCurriculum(id, next, userId);
      setItems(prev => prev.map(i => i.id === id ? { ...i, title: next } : i));
    } catch { /* title reverts in UI since we haven't committed to local state yet */ }
  }

  return (
    <div className="space-y-6">
      {/* Delete confirmation modal */}
      <AnimatePresence>
        {deleteConfirmId !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full z-10">
              <p className="text-slate-800 font-semibold text-center mb-1">Delete Curriculum?</p>
              <p className="text-slate-500 text-sm text-center mb-4">
                This curriculum will be permanently removed from your Hub. This action cannot be undone.
              </p>
              {deleteError && (
                <p className="text-xs text-red-600 text-center mb-4 flex items-center justify-center gap-1.5">
                  <AlertCircle size={12} /> {deleteError}
                </p>
              )}
              <div className="flex gap-3">
                <button onClick={() => { setDeleteConfirmId(null); setDeleteError(null); }}
                  className="flex-1 h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirmId!)} disabled={deleting}
                  className="flex-1 h-10 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-semibold transition-colors">
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center">
            <Library size={18} className="text-teal-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 leading-tight">Curriculum Hub</h2>
            <p className="text-xs text-slate-500">Your saved curricula — the source for curriculum-based lesson generation.</p>
          </div>
        </div>
        <Button onClick={onUploadNew}
          className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white gap-1.5">
          <Plus size={15} /> Add Curriculum
        </Button>
      </div>

      {/* Scope tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit flex-wrap">
        {SCOPE_TABS.map(tab => (
          <button key={tab.id} onClick={() => setScope(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              scope === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Scope access note for non-my scopes */}
      {scope !== 'my' && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-center gap-2">
          <AlertCircle size={13} className="flex-shrink-0" />
          {scope === 'team'
            ? 'Team / PLC curricula will be visible once team sharing is enabled.'
            : scope === 'school'
            ? 'School curricula are managed by school administrators.'
            : 'District curricula are managed by district administrators.'}
          {' '}Role-based access is coming soon.
        </div>
      )}

      {/* Search + refresh */}
      {scope === 'my' && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by title, subject, grade, or publisher…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-10 rounded-xl border-slate-200 pl-9 text-sm" />
          </div>
          <button onClick={load} title="Refresh"
            className="h-10 w-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-all">
            <RefreshCw size={14} />
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <svg className="animate-spin w-7 h-7 text-teal-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
        </div>
      ) : error ? (
        <div className="bg-red-50 rounded-2xl border border-red-200 p-6 text-center">
          <AlertCircle size={22} className="text-red-400 mx-auto mb-2" />
          <p className="text-sm text-red-700 font-medium">{error}</p>
          <button onClick={load} className="mt-3 text-sm text-red-600 hover:text-red-800 font-semibold transition-colors">
            Try again
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-16 flex flex-col items-center gap-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center">
            {search ? <Filter size={24} className="text-slate-300" /> : <Library size={24} className="text-slate-300" />}
          </div>
          <div>
            <p className="font-semibold text-slate-600 mb-1">
              {search ? 'No curricula match your search' : 'No curricula saved yet'}
            </p>
            <p className="text-slate-400 text-sm max-w-xs mx-auto leading-relaxed">
              {search
                ? 'Try a different search term.'
                : 'Upload your first curriculum to start generating curriculum-based lesson plans.'}
            </p>
          </div>
          {!search && (
            <Button onClick={onUploadNew}
              className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white gap-1.5">
              <Upload size={15} /> Upload Curriculum
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          <AnimatePresence>
            {filtered.map(item => (
              <motion.div key={item.id}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all flex flex-col">

                {/* Card body */}
                <div className="p-5 flex-1">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0 text-lg">
                      {SOURCE_ICONS[item.sourceType] ?? '📄'}
                    </div>
                    <div className="flex-1 min-w-0">
                      {renameId === item.id ? (
                        <div className="flex items-center gap-1.5 mb-1">
                          <input
                            ref={renameRef}
                            value={renameVal}
                            onChange={e => setRenameVal(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') commitRename(item.id);
                              if (e.key === 'Escape') setRenameId(null);
                            }}
                            onBlur={() => commitRename(item.id)}
                            className="flex-1 text-sm font-semibold text-slate-900 bg-white border border-teal-400/50 rounded-lg px-2 py-0.5 outline-none focus:ring-2 focus:ring-teal-500/20"
                          />
                          <button onClick={() => commitRename(item.id)}
                            className="h-6 w-6 rounded-md bg-teal-600 text-white flex items-center justify-center flex-shrink-0">
                            <Check size={11} />
                          </button>
                          <button onClick={() => setRenameId(null)}
                            className="h-6 w-6 rounded-md border border-slate-200 text-slate-400 flex items-center justify-center flex-shrink-0">
                            <X size={11} />
                          </button>
                        </div>
                      ) : (
                        <p className="font-bold text-slate-900 text-sm leading-snug line-clamp-2">
                          {item.title || item.fileName || 'Untitled Curriculum'}
                        </p>
                      )}
                      <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                        {SOURCE_LABELS[item.sourceType] ?? item.sourceType}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-slate-500">
                    {(item.subject || item.gradeLevel) && (
                      <p>{[item.subject, item.gradeLevel].filter(Boolean).join(' · ')}</p>
                    )}
                    {item.publisher && (
                      <p className="truncate text-slate-400">Publisher: {item.publisher}</p>
                    )}
                    <p>Added {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                </div>

                {/* Card actions */}
                <div className="border-t border-slate-100 px-4 py-3 flex items-center gap-2">
                  <button onClick={() => onUseInLesson(item.id)}
                    className="flex-1 h-8 rounded-lg bg-primary hover:bg-primary/90 text-white text-xs font-semibold transition-colors flex items-center justify-center gap-1">
                    Use in Lesson <ArrowRight size={11} />
                  </button>
                  <button onClick={() => startRename(item.id, item.title)} title="Rename"
                    className="h-8 w-8 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-all">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => { setDeleteConfirmId(item.id); setDeleteError(null); }} title="Delete"
                    className="h-8 w-8 rounded-lg border border-slate-200 hover:bg-red-50 hover:border-red-200 flex items-center justify-center text-slate-400 hover:text-red-500 transition-all">
                    <Trash2 size={14} />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Footer note */}
      {scope === 'my' && items.length > 0 && (
        <p className="text-xs text-slate-400 text-center">
          {items.length} curriculum{items.length !== 1 ? 'a' : ''} in My Curriculum ·{' '}
          Team, School, and District sharing coming soon
        </p>
      )}
    </div>
  );
}
